import { Email, Phone, WhatsApp } from "@mui/icons-material";
import SecurityIcon from "@mui/icons-material/Security";
import ShieldIcon from "@mui/icons-material/Shield";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";

export function PrivacyAprendeMais() {
  const theme = useTheme();
  const primaryColor = "#060040";

  const sections = [
    {
      icon: <VisibilityIcon />,
      title: "1. Coleta de Informações",
      content: "O Aprende Mais não coleta, armazena ou processa qualquer tipo de dado pessoal identificável dos usuários.",
      points: [
        "Não solicitamos informações como e-mail, número de telefone ou qualquer outra informação pessoal",
        "O nome é opcional quando usuário partilha algum conteúdo",
        "Não coletamos dados de localização ou de uso do dispositivo",
        "Não utilizamos cookies ou outras tecnologias de rastreamento",
      ],
    },
    {
      icon: <WhatsApp />,
      title: "2. Compartilhamento de Conteúdo na Biblioteca",
      content: "O Aprende Mais oferece uma funcionalidade de Biblioteca, onde os usuários podem compartilhar livros, artigos e outros materiais educativos.",
      points: [
        "Quando você opta por compartilhar conteúdo na Biblioteca, o aplicativo redireciona você para o WhatsApp",
        "O Aprende Mais não coleta, armazena ou acessa os conteúdos compartilhados",
        "A troca de informações ocorre diretamente no WhatsApp, sob seus termos e políticas de privacidade",
      ],
    },
    {
      icon: <ShieldIcon />,
      title: "3. Uso de Informações",
      content: "Como não coletamos nenhum tipo de informação pessoal, não utilizamos dados para nenhum fim comercial:",
      points: [
        "❌ Marketing personalizado",
        "❌ Publicidade direcionada",
        "❌ Análise de comportamento de uso",
      ],
      note: "O único objetivo do Aprende Mais é oferecer uma experiência interativa de aprendizado e entretenimento.",
    },
    {
      icon: <SecurityIcon />,
      title: "4. Segurança das Informações",
      content: "Embora o Aprende Mais não armazene informações pessoais, garantimos que o aplicativo foi desenvolvido seguindo as melhores práticas de segurança:",
      points: [
        "✅ Código seguro e atualizado regularmente",
        "✅ Nenhum acesso não autorizado a dados sensíveis",
        "✅ Proteção da sua experiência de uso",
      ],
    },
    {
      icon: <ShieldIcon />,
      title: "5. Proteção de Dados de Crianças",
      content: "O Aprende Mais é destinado a usuários de todas as idades. No entanto, o aplicativo não coleta informações de crianças ou menores de idade.",
      points: [],
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box
        sx={{
          textAlign: "center",
          mb: 6,
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: -16,
            left: "50%",
            transform: "translateX(-50%)",
            width: 100,
            height: 4,
            background: `linear-gradient(90deg, ${primaryColor}, ${alpha(primaryColor, 0.3)})`,
            borderRadius: 2,
          },
        }}
      >
        <SecurityIcon
          sx={{
            fontSize: 64,
            color: primaryColor,
            mb: 2,
            opacity: 0.9,
          }}
        />
        <Typography
          variant="h3"
          component="h1"
          fontWeight="bold"
          sx={{
            color: primaryColor,
            mb: 2,
            fontSize: { xs: "2rem", md: "2.5rem" },
          }}
        >
          Política de Privacidade
        </Typography>
        <Typography
          variant="h5"
          color="text.secondary"
          fontWeight={400}
          sx={{ mb: 1 }}
        >
          Aprende Mais
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Última atualização: Agosto 2026
        </Typography>
      </Box>

      {/* Intro Card */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 6,
          background: `linear-gradient(135deg, ${alpha(primaryColor, 0.05)} 0%, ${alpha(primaryColor, 0.1)} 100%)`,
          border: `1px solid ${alpha(primaryColor, 0.2)}`,
          borderRadius: 3,
        }}
      >
        <Typography variant="body1" sx={{ lineHeight: 1.8, color: "text.primary" }}>
          Bem-vindo à <strong>Política de Privacidade</strong> do Aprende Mais.
          Levamos a sua privacidade a sério e este documento descreve como protegemos
          as suas informações e dados pessoais.
        </Typography>
      </Paper>

      {/* Main Content */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {sections.map((section, index) => (
          <Card
            key={index}
            elevation={2}
            sx={{
              borderRadius: 3,
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: theme.shadows[8],
              },
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.7)} 100%)`,
                p: 3,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  backgroundColor: alpha("#fff", 0.2),
                  borderRadius: "50%",
                  width: 48,
                  height: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {React.cloneElement(section.icon, {
                  sx: { color: "#fff", fontSize: 28 },
                })}
              </Box>
              <Typography
                variant="h6"
                component="h2"
                fontWeight="bold"
                sx={{ color: "#fff" }}
              >
                {section.title}
              </Typography>
            </Box>
            <CardContent sx={{ p: 4 }}>
              <Typography
                variant="body1"
                sx={{ mb: 3, lineHeight: 1.8, color: "text.primary" }}
              >
                {section.content}
              </Typography>
              {section.points && section.points.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    pl: 2,
                    borderLeft: `3px solid ${alpha(primaryColor, 0.3)}`,
                  }}
                >
                  {section.points.map((point, pointIndex) => (
                    <Box
                      key={pointIndex}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: primaryColor,
                          mt: 1,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ lineHeight: 1.6, flex: 1 }}
                      >
                        {point}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              {section.note && (
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    backgroundColor: alpha(primaryColor, 0.05),
                    borderRadius: 2,
                    border: `1px solid ${alpha(primaryColor, 0.2)}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    fontWeight="medium"
                    sx={{ color: primaryColor }}
                  >
                    💡 {section.note}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* Contact Section */}
      <Paper
        elevation={3}
        sx={{
          p: 4,
          background: `linear-gradient(135deg, ${alpha(primaryColor, 0.08)} 0%, ${alpha(primaryColor, 0.12)} 100%)`,
          border: `2px solid ${alpha(primaryColor, 0.2)}`,
          borderRadius: 3,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h5"
          component="h2"
          fontWeight="bold"
          gutterBottom
          sx={{ color: primaryColor, mb: 3 }}
        >
          📞 Entre em Contato
        </Typography>
        <Typography
          variant="body1"
          sx={{ mb: 4, lineHeight: 1.8 }}
        >
          Se você tiver dúvidas ou preocupações sobre esta Política de Privacidade
          ou sobre o uso do Aprende Mais, entre em contacto conosco:
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 4,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 3,
              py: 2,
              backgroundColor: alpha("#fff", 0.8),
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Email sx={{ color: primaryColor }} />
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="caption" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                franciscotamelle@gmail.com
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 3,
              py: 2,
              backgroundColor: alpha("#fff", 0.8),
              borderRadius: 2,
              boxShadow: 1,
            }}
          >
            <Phone sx={{ color: primaryColor }} />
            <Box sx={{ textAlign: "left" }}>
              <Typography variant="caption" color="text.secondary">
                Telefone
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                +258 845857656
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Footer */}
      <Box
        sx={{
          textAlign: "center",
          mt: 6,
          py: 3,
          borderTop: `1px solid ${alpha(primaryColor, 0.1)}`,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          © 2026 Aprende Mais. Todos os direitos reservados.
        </Typography>
        <Chip
          label="Privacidade em Primeiro Lugar"
          size="small"
          sx={{
            mt: 2,
            backgroundColor: alpha(primaryColor, 0.1),
            color: primaryColor,
            fontWeight: "medium",
          }}
        />
      </Box>
    </Container>
  );
}

export default PrivacyAprendeMais;